import FlexBox from "lib/components/common/flex-box";
import { Formik } from "formik";
import SimpleDropdown from "lib/components/common/simple-dropdown";
import SimplePasswordField from "lib/components/common/simple-password";
import SimpleTextbox from "lib/components/common/simple-textbox";
import { BUSINESS_CATEGORIES } from "lib/constants";

import {
  NAME_VALIDATION_SCHEMA,
  REQUIRED_FIELD_VALIDATION_SCHEMA,
  WEBSITE_VALIDATION_SCHEMA,
  EMAIL_VALIDATION_SCHEMA,
  PHONE_NUMBER_VALIDATION_SCHEMA,
  SIGN_UP_VALIDATION_SCHEME,
  SIGN_UP_BIO_VALIDATION_SCHEME,
  PASSWORD_VALIDATION_SCHEMA,
} from "lib/constants/validation-constants";
import { getArrayFrom } from "lib/utils/array-utils";
import { Row, Col, Button, Form, Spinner } from "react-bootstrap";
import { FC } from "react";
import { DEFAULT_PRACTITIONER_SIGNUP_REQUEST_VALUE } from "lib/constants/default-values";
import ErrorBox from "lib/components/common/error-box";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_V2_SITE_KEY || '';

interface PractitionerSignupBioProps {
  isLoading?: boolean;
  error?: string;
  onCategoryChange?: (category: string) => void;
  onSubmit?: (values: PractitionerDetails) => void;
}

const PractitionerSignupBio: FC<PractitionerSignupBioProps> = ({
  isLoading,
  error,
  onCategoryChange,
  onSubmit,
}) => {
  return (
    <>
      {error && <ErrorBox message={error} />}
      <Formik
        initialValues={DEFAULT_PRACTITIONER_SIGNUP_REQUEST_VALUE.bio}
        validationSchema={SIGN_UP_BIO_VALIDATION_SCHEME}
        onSubmit={async (values: PractitionerDetails) => {
          if (!values.recaptchaToken) {
            alert("Please verify you are not a robot.");
            return;
          }
          onSubmit?.({ ...values, businessCategory: "organisation" });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form onSubmit={handleSubmit}>
            {/* <SimpleDropdown
              label="Signup As"
              name="businessCategory"
              className="form-control"
              options={getArrayFrom(BUSINESS_CATEGORIES)}
              values={getArrayFrom(BUSINESS_CATEGORIES)}
              selectedValue={values.businessCategory}
              onChange={(e) => {
                handleChange(e);
                onCategoryChange?.(e.target.value);
              }}
              required
            /> */}

            {values.businessCategory === BUSINESS_CATEGORIES.ORGANISATION && (
              <fieldset>
                <legend>Organisation Details</legend>
                <SimpleTextbox
                  label="Business Name"
                  validationSchema={NAME_VALIDATION_SCHEMA(
                    "Please provide business name"
                  )}
                  className="form-control"
                  type="text"
                  name="businessName"
                  value={values.businessName}
                  onChange={handleChange}
                  required
                  error={errors.businessName}
                />
                <SimpleTextbox
                  as="textarea"
                  label="Business Summary"
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA()}
                  className="form-control h-100"
                  name="businessSummary"
                  value={values.businessSummary}
                  onChange={handleChange}
                  required
                  error={errors.businessSummary}
                />
                <SimpleTextbox
                  label="Website Link"
                  type="url"
                  name="websiteLink"
                  value={values.websiteLink}
                  onChange={handleChange}
                  validationSchema={WEBSITE_VALIDATION_SCHEMA}
                  error={errors.websiteLink}
                />
              </fieldset>
            )}

            <fieldset>
              <legend>Personal Details</legend>

              <Row>
                <Col md={6}>
                  <SimpleTextbox
                    label="First Name"
                    type="text"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    required
                    validationSchema={NAME_VALIDATION_SCHEMA(
                      "Please provide first name"
                    )}
                    error={errors.firstName}
                  />
                </Col>
                <Col md={6}>
                  <SimpleTextbox
                    label="Last Name"
                    type="text"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    required
                    validationSchema={NAME_VALIDATION_SCHEMA(
                      "Please provide last name"
                    )}
                    error={errors.lastName}
                  />
                </Col>
              </Row>
              <SimpleTextbox
                label="Email address"
                type="email"
                name="email"
                value={values.email}
                className="form-control"
                onChange={handleChange}
                required
                validationSchema={EMAIL_VALIDATION_SCHEMA}
                error={errors.email}
              />
              <SimpleTextbox
                label="Contact No."
                type="tel"
                name="contactNumber"
                value={values.contactNumber}
                onChange={handleChange}
                required
                validationSchema={PHONE_NUMBER_VALIDATION_SCHEMA}
              />
              <SimpleTextbox
                as="textarea"
                label="About you"
                validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA()}
                className="form-control h-100"
                name="biography"
                value={values.biography}
                onChange={handleChange}
              />
            </fieldset>

            <fieldset>
              <legend>Passwords</legend>
              <SimplePasswordField
                label="Password"
                name="password"
                value={values.password}
                onChange={handleChange}
                required
                isShowVisibilityIcon
                validationSchema={PASSWORD_VALIDATION_SCHEMA}
              />
              <SimplePasswordField
                isShowVisibilityIcon
                name="confirmPassword"
                label="Confirm Password"
                required
                value={values.confirmPassword}
                onChange={handleChange}
                validationSchema={PASSWORD_VALIDATION_SCHEMA}
                error={
                  touched.confirmPassword &&
                  values.confirmPassword &&
                  values.password !== values.confirmPassword
                    ? "Passwords do not match"
                    : null
                }
              />
            </fieldset>

            {/* Add reCAPTCHA */}
            <div className="mb-3 text-center">
              <ReCAPTCHA
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) =>
                  setFieldValue("recaptchaToken", token)
                }
              />
            </div>

            <Form.Group className="form-group login-btn text-center">
              <Button
                className="btn btn-primary px-4"
                type="submit"
                disabled={isLoading}
              >
                Sign up{" "}
                {isLoading && <Spinner animation="border" variant="warning" />}
              </Button>
            </Form.Group>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PractitionerSignupBio;
